import React from 'react'
import { store } from '../store'
import { updateTimer, handleDisconnect, syncWithServer } from '../store/slices/timerSlice'
import { getSessionStatus } from '../store/slices/testSlice'
import { config } from '../config/env'
import toast from 'react-hot-toast'

interface TimerUpdate {
  type: 'timer_update'
  session_id: string
  remaining_time_seconds: number
  status: string
  timestamp: string
}

interface SessionExpired {
  type: 'session_expired'
  session_id: string
  message: string
}

interface SessionEvent {
  type: 'session_event'
  session_id: string
  event_type: string
  event_data: any
}

interface WebSocketError {
  type: 'error'
  message: string
}

type WebSocketMessage = TimerUpdate | SessionExpired | SessionEvent | WebSocketError

class WebSocketService {
  private socket: WebSocket | null = null
  private currentSessionId: string | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000 // Start with 1 second
  private isConnecting = false
  private heartbeatInterval: NodeJS.Timeout | null = null
  private pendingMessages: Array<{ message: any; key?: string }> = []

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handleOnline = this.handleOnline.bind(this)
    this.handleOffline = this.handleOffline.bind(this)
    
    // Listen for browser events
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
  }

  private enqueuePendingMessage(message: any, key?: string) {
    if (key) {
      const existingIndex = this.pendingMessages.findIndex(entry => entry.key === key)
      if (existingIndex >= 0) {
        this.pendingMessages[existingIndex] = { message, key }
        return
      }
    }

    this.pendingMessages.push({ message, key })
  }

  connect(sessionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('WebSocket connect called for session:', sessionId)
      console.log('Current state:', {
        isConnecting: this.isConnecting,
        socketState: this.socket?.readyState,
        currentSessionId: this.currentSessionId
      })

      const token = localStorage.getItem('access_token')
      if (!token) {
        this.isConnecting = false
        console.log('No authentication token, continuing without WebSocket...')
        resolve()
        return
      }

      const joinMessage = {
        type: 'join_session',
        session_id: sessionId,
        token: token
      }
      const joinMessageKey = `join:${sessionId}`

      this.currentSessionId = sessionId
      this.enqueuePendingMessage(joinMessage, joinMessageKey)

      if (this.isConnecting) {
        console.log('WebSocket connection already in progress, join message queued...')
        resolve()
        return
      }

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected, flushing pending messages...')
        this.flushPendingMessages()
        resolve()
        return
      }

      // If we have a socket that's not open, close it first
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        console.log('Closing existing WebSocket connection...')
        this.socket.close()
        this.socket = null
      }

      this.isConnecting = true

      try {
        // Create WebSocket connection
        // Normalize URL: config.WS_URL may already include "/ws"; avoid double-appending
        const baseWsUrl = config.WS_URL.replace('http://', 'ws://').replace('https://', 'wss://')
        const finalWsUrl = baseWsUrl.endsWith('/ws') ? baseWsUrl : `${baseWsUrl}/ws`
        console.log('Creating WebSocket connection to:', finalWsUrl)
        this.socket = new WebSocket(finalWsUrl)

        // Set a timeout for connection - shorter in production for better UX
        const connectionTimeout = setTimeout(() => {
          if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
            console.log('[WebSocket] Connection timeout after 5 seconds')
            this.socket.close()
            this.isConnecting = false
            // Don't fail the test start if WebSocket fails
            resolve()
          }
        }, 5000) // 5 second timeout for production

        // Connection events
        this.socket.onopen = () => {
          clearTimeout(connectionTimeout)
          console.log('WebSocket connected successfully')
          this.isConnecting = false
          this.reconnectAttempts = 0
          this.reconnectDelay = 1000
          this.flushPendingMessages()
          
          // Start heartbeat
          this.startHeartbeat()
          
          resolve()
        }

        this.socket.onerror = (error) => {
          clearTimeout(connectionTimeout)
          console.error('WebSocket connection error:', error)
          this.isConnecting = false
          // Don't reject immediately, try to continue without WebSocket
          console.log('Continuing without WebSocket connection...')
          // Don't trigger reconnection on error - let the connection close naturally
          resolve()
        }

        this.socket.onclose = (event) => {
          clearTimeout(connectionTimeout)
          console.log('WebSocket disconnected:', event.code, event.reason)
          this.isConnecting = false
          this.stopHeartbeat()
          
          if (event.code === 1000) {
            // Normal closure, don't reconnect
            this.handleServerDisconnect()
          } else if (event.code === 1006) {
            // Abnormal closure, try to reconnect only once
            console.log('Abnormal closure detected, attempting single reconnection...')
            if (this.reconnectAttempts === 0) {
              this.handleReconnect()
            } else {
              console.log('Already attempted reconnection, stopping...')
            }
          } else {
            // Other network issues, try to reconnect only once
            if (this.reconnectAttempts === 0) {
              this.handleReconnect()
            } else {
              console.log('Already attempted reconnection, stopping...')
            }
          }
        }

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            console.log('WebSocket message received:', data)
            this.handleMessage(data)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

      } catch (error) {
        console.error('Failed to create WebSocket connection:', error)
        this.isConnecting = false
        resolve()
      }
    })
  }

  private handleMessage(data: any) {
    console.log('Handling WebSocket message type:', data.type)
    switch (data.type) {
      case 'welcome':
        console.log('WebSocket welcome:', data.message)
        break
        
      case 'timer_update':
        store.dispatch(updateTimer({
          remainingTimeSeconds: data.remaining_time_seconds,
          lastSyncTime: data.timestamp,
        }))
        break
        
      case 'session_expired':
        toast.error(data.message, {
          duration: 5000,
          style: {
            background: '#dc2626',
            color: 'white',
          },
        })
        this.handleSessionExpired(data.session_id)
        break
        
      case 'session_event':
        this.handleSessionEvent(data)
        break
        
      case 'error':
        console.error('WebSocket error:', data.message)
        toast.error(`Connection error: ${data.message}`)
        break
        
      case 'timer_sync':
        store.dispatch(syncWithServer({
          remainingTimeSeconds: data.remaining_time_seconds,
          pauseCount: data.pause_count,
          disconnectCount: data.disconnect_count,
          lastSyncTime: data.last_sync_time,
        }))
        break
        
      case 'heartbeat_ack':
        // Heartbeat acknowledged
        break
        
      default:
        console.log('Unknown WebSocket message type:', data.type)
    }
  }

  private sendMessage(message: any) {
    if (!this.socket) {
      console.log('WebSocket instance missing, queueing message for later:', message.type)
      this.enqueuePendingMessage(message)
      return
    }

    if (this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(message))
      } catch (error) {
        console.error('Error sending WebSocket message:', error)
      }
      return
    }

    if (this.socket.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket still connecting, queueing message:', message.type)
      this.enqueuePendingMessage(message)
      return
    }

    console.log('WebSocket not ready, queueing message for next connection:', message.type)
    this.enqueuePendingMessage(message)
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.currentSessionId) {
        this.sendMessage({
          type: 'heartbeat',
          session_id: this.currentSessionId,
          timestamp: new Date().toISOString(),
        })
      }
    }, 30000) // Send heartbeat every 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  disconnect() {
    this.stopHeartbeat()
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
    this.currentSessionId = null
    this.reconnectAttempts = 0
    this.isConnecting = false
    this.pendingMessages = []
  }

  // Request manual timer sync
  requestTimerSync() {
    if (this.socket && this.currentSessionId) {
      this.sendMessage({
        type: 'sync_timer',
        session_id: this.currentSessionId
      })
    }
  }

  // Send heartbeat to maintain connection
  sendHeartbeat() {
    if (this.socket && this.currentSessionId) {
      this.sendMessage({
        type: 'heartbeat',
        session_id: this.currentSessionId,
        timestamp: new Date().toISOString(),
      })
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log(`Max reconnection attempts reached (${this.maxReconnectAttempts}) - continuing without WebSocket`)
      return
    }

    if (this.isConnecting) {
      console.log('Reconnection already in progress, skipping...')
      return
    }

    this.reconnectAttempts++
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 10000)

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    setTimeout(() => {
      if (this.currentSessionId && (!this.socket || this.socket.readyState !== WebSocket.OPEN) && !this.isConnecting) {
        // Record disconnect in Redux
        store.dispatch(handleDisconnect())
        
        // Attempt reconnection
        this.connect(this.currentSessionId).catch((error) => {
          console.log('Reconnection failed - continuing without WebSocket:', error)
        })
      }
    }, delay)
  }

  private handleServerDisconnect() {
    console.log('Server connection lost - continuing without WebSocket')
  }

  private async handleSessionExpired(sessionId: string) {
    // Get latest session status and trigger auto-submit
    try {
      await store.dispatch(getSessionStatus(sessionId))
      // The session should now be marked as expired/auto-submitted
      
      // Redirect to completion page
      window.location.href = `/test/${sessionId}/completed`
    } catch (error) {
      console.error('Error handling session expiry:', error)
    }
  }

  private handleSessionEvent(data: SessionEvent) {
    switch (data.event_type) {
      case 'session_paused':
        toast('Session paused', { duration: 3000, icon: 'ℹ️' })
        break
      case 'session_resumed':
        toast.success('Session resumed', { duration: 3000 })
        break
      case 'proctor_warning':
        toast(`Proctoring alert: ${data.event_data.message}`, {
          duration: 5000,
          icon: '⚠️'
        })
        break
      case 'connection_warning':
        toast('Unstable connection detected', { duration: 4000, icon: '⚠️' })
        break
      default:
        console.log('Unknown session event:', data)
    }
  }

  private handleVisibilityChange() {
    // Check if tab switch warnings are disabled for this session
    const state = store.getState()
    const currentSession = state.test?.currentSession
    const disableTabSwitchWarnings = currentSession?.disable_tab_switch_warnings
    
    if (document.hidden) {
      // Page is hidden - send notification about potential focus loss
      // BUT only if tab switch warnings are NOT disabled
      if (this.socket && this.currentSessionId && !disableTabSwitchWarnings) {
        this.sendMessage({
          type: 'focus_lost',
          session_id: this.currentSessionId,
          timestamp: new Date().toISOString(),
        })
      } else if (disableTabSwitchWarnings) {
        console.log('[WebSocket] Tab switch warnings disabled for this session - ignoring focus loss')
      }
    } else {
      // Page is visible again - request timer sync immediately
      if (this.socket && this.currentSessionId) {
        this.sendMessage({
          type: 'sync_timer',
          session_id: this.currentSessionId,
          timestamp: new Date().toISOString(),
        })
      }
    }
  }

  private handleOnline() {
    toast.success('Connection restored', { duration: 2000 })
    if (this.currentSessionId && (!this.socket || this.socket.readyState !== WebSocket.OPEN)) {
      this.connect(this.currentSessionId).catch(console.error)
    }
  }

  private handleOffline() {
    toast.error('Connection lost', { duration: 4000 })
    store.dispatch(handleDisconnect())
  }

  private flushPendingMessages() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }

    const pendingCount = this.pendingMessages.length
    if (pendingCount > 0) {
      console.log(`Flushing ${pendingCount} queued WebSocket message(s)`)
    }

    while (this.pendingMessages.length > 0) {
      const entry = this.pendingMessages.shift()
      if (!entry) {
        continue
      }

      const { message, key } = entry
      try {
        console.log('Sending queued WebSocket message:', message?.type || key || 'unknown')
        this.socket.send(JSON.stringify(message))
      } catch (error) {
        console.error('Error sending queued WebSocket message:', error)
        this.pendingMessages.unshift(entry)
        break
      }
    }
  }

  // Public methods for external use
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  getConnectionStatus(): {
    connected: boolean
    reconnectAttempts: number
    currentSessionId: string | null
  } {
    return {
      connected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      currentSessionId: this.currentSessionId,
    }
  }

  // Clean up event listeners
  destroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    this.stopHeartbeat()
    this.disconnect()
  }
}

// Create singleton instance
export const webSocketService = new WebSocketService()

// Hook for React components
export const useWebSocket = (sessionId?: string) => {
  const connect = React.useCallback((id: string) => webSocketService.connect(id), [])
  const disconnect = React.useCallback(() => webSocketService.disconnect(), [])
  const requestSync = React.useCallback(() => webSocketService.requestTimerSync(), [])
  const isConnected = React.useCallback(() => webSocketService.isConnected(), [])
  const getStatus = React.useCallback(() => webSocketService.getConnectionStatus(), [])

  return {
    connect,
    disconnect,
    requestSync,
    isConnected,
    getStatus,
  }
}

export default webSocketService
